import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, styled, Typography } from '@mui/material';
import { IconDocument } from 'assets/img';
import { FormProvider, useFormContext } from 'react-hook-form';
import { DynamicForm } from 'redesign';
import { SignDocumentDialogBodyProps } from './sign-document-dialog-body.types';
import { getDocumentStatusIcon } from '../../../../../table/utils/get-document-status-icon';
import { useDisplayDocument } from '../../../../../table/hooks/use-display-document';

export const SignDocumentDialogBody = ({
  type,
  status,
  selectedDocumentModel,
}: SignDocumentDialogBodyProps) => {
  const { t } = useTranslation(['web-common', 'hq-screen-specific']);
  const methods = useFormContext();
  const { handleDownload } = useDisplayDocument(methods.getValues('documentId'));

  const key = `documents.status.${status.toLowerCase()}`;

  return (
    <FormProvider {...methods}>
      <FlexColumnBoxWrap>
        <DocumentInfo>
          <DocumentWrap>
            <FlexRowBoxCenter>
              <IconDocument width={130} height={150} />
            </FlexRowBoxCenter>
            <ButtonWrapDocument>
              <OpenDocumentButton onClick={handleDownload} role="button">
                {t('button.open', { ns: 'web-common' })}
              </OpenDocumentButton>
            </ButtonWrapDocument>
          </DocumentWrap>
          <DocumentInfoWrapper>
            <Stack direction="row" spacing={5} alignItems="center">
              <DocumentName>{type}</DocumentName>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body1" noWrap>
                  {t(key, { ns: 'web-common' })}
                </Typography>
                <StyledIcon as={getDocumentStatusIcon(status)} />
              </Box>
            </Stack>
            <FormBox>
              <DynamicForm config={selectedDocumentModel} />
            </FormBox>
          </DocumentInfoWrapper>
        </DocumentInfo>
      </FlexColumnBoxWrap>
    </FormProvider>
  );
};

const FlexColumnBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const FlexRowBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
}));

const FlexColumnBoxWrap = styled(FlexColumnBox)(() => ({
  overflow: 'auto',
}));

const DocumentInfo = styled(FlexRowBox)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const DocumentWrap = styled(FlexColumnBox)(({ theme }) => ({
  alignItems: 'flex-start',
  marginRight: theme.spacing(8),
}));

const FlexRowBoxCenter = styled(FlexRowBox)(() => ({
  alignItems: 'center',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const ButtonWrapDocument = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(1),
}));

const OpenDocumentButton = styled(Button)(({ theme }) => ({
  '&.MuiButton-root': {
    width: '100%',
    border: '1px solid rgba(0,0,0,0.3)',
    borderRadius: theme.spacing(7),
    padding: '8px 10px',
    fontWeight: 700,
    fontSize: '12px',
    color: 'rgba(0,0,0,0.6)',
  },
}));

const DocumentInfoWrapper = styled(FlexColumnBox)(({ theme }) => ({
  gap: theme.spacing(4),
  width: '100%',
}));

const DocumentName = styled(Typography)(() => ({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '24px',
}));

const FormBox = styled(Box)(({ theme }) => ({
  gap: theme.spacing(4),
  display: 'flex',
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  width: theme.spacing(5),
  height: theme.spacing(5),
  marginRight: theme.spacing(1.5),
}));
